import type { NavItemProps } from '../types';

import { useMemo } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import { Box, Link } from '@/components/ui';
import { Iconify } from '@/components/iconify';

import { createNavItem, navSectionClasses } from '../components/nav-section-classes';

// ----------------------------------------------------------------------

export const NavItem = ({
  path,
  icon,
  title,
  type,
  open,
  active,
  subItem,
  subHeader,
  hasChild,
  className,
  externalLink,
  onClick,
  color,
  ...other
}: NavItemProps) => {
  const navItem = useMemo(
    () =>
      createNavItem({
        path,
        icon,
        hasChild,
        externalLink,
      }),
    [path, icon, hasChild, externalLink]
  );

  const linkOnClick = type === 'folder' || hasChild ? onClick : undefined;
  const isFolder = type === 'folder' || hasChild;

  // Memoize common class configurations
  const commonClasses = useMemo(
    () =>
      mergeClasses(
        [
          navSectionClasses.item.root,
          'flex w-full px-4 py-2.5 pr-[40px] uppercase text-left text-base items-center text-black',
          open ? 'text-black bg-primary text-white' : undefined,
          active ? 'text-white bg-primary hover:bg-primary' : undefined,
          className,
        ],
        {
          [navSectionClasses.state.open]: open,
          [navSectionClasses.state.active]: active,
        }
      ),
    [open, active, className]
  );

  const titleClasses = useMemo(
    () =>
      mergeClasses([
        navSectionClasses.item.title,
        'flex-grow text-base font-normal',
        subItem ? 'text-xs' : undefined,
        subHeader ? 'text-sm underline underline-offset-2 text-primary' : undefined,
        active ? 'font-semibold' : undefined,
      ]),
    [subItem, subHeader, active]
  );

  const iconClasses = useMemo(
    () => mergeClasses(['text-violet', ...(active ? ['text-white'] : [])]),
    [active]
  );

  const arrowClasses = useMemo(
    () =>
      mergeClasses([
        navSectionClasses.item.arrow,
        open || active ? 'text-white' : 'text-black',
        'absolute top-1/2 right-[16px] -translate-y-1/2 flex items-center justify-center transition-transform duration-200 ease-in-out cursor-pointer',
      ]),
    [open, active]
  );

  // Render icon component
  const renderIcon = () => {
    if (!navItem.renderIcon) return null;

    return (
      <span className="flex mr-2 flex-shrink-0 ">
        {typeof icon === 'string' ? (
          <Iconify
            icon={navItem.renderIcon as any}
            className={iconClasses}
            width={20}
            height={20}
          />
        ) : (
          navItem.renderIcon
        )}
      </span>
    );
  };

  // Render title component
  const renderTitle = () => <span className={titleClasses}>{title}</span>;

  // Render content (icon + title)
  const renderContent = () => (
    <>
      {renderIcon()}
      {renderTitle()}
    </>
  );

  return (
    <Box className="relative w-full">
      {isFolder ? (
        <Box onClick={linkOnClick} className={commonClasses} {...other}>
          {renderContent()}
        </Box>
      ) : (
        <Link
          aria-label={title}
          href={navItem.baseProps.href || '#'}
          onClick={linkOnClick}
          className={commonClasses}
          {...other}
        >
          {renderContent()}
        </Link>
      )}

      {hasChild && (
        <span className={arrowClasses} onClick={onClick}>
          <Iconify icon="lucide:chevron-right" width={20} height={20} />
        </span>
      )}
    </Box>
  );
};
